import './LobbyTablesExpertMode.styles.scss';

import { observer } from 'mobx-react';
import { useDebounce } from 'primereact/hooks';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { UPDATE_LOBBY_EXPERT_MODE_TIMEOUT } from '../../../../constants';
import { LobbyTableCustomSortingProperty } from '../../../../model/lobbytable/LobbyTableCustomSorting';
import { LobbyTableResponse } from '../../../../model/lobbytable/LobbyTableResponse';
import { SortDirection } from '../../../../model/SortDirection';
import lobbyTableStore from '../../../../store/LobbyTableStore';
import tableStore from '../../../../store/TableStore';
import LobbyTable from '../../../Dashboard/LobbyTable/LobbyTable';
import SortingInput from '../../../shared/SortingInput/SortingInput';
import { LobbyTablesExpertModeProps } from './LobbyTablesExpertMode.types';

const PAGE_SIZE = 10;

const LobbyTablesExpertMode: FC<LobbyTablesExpertModeProps> = ({ tableId }) => {
  const [lobbyTables, setLobbyTables] = useState<LobbyTableResponse[]>([]);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce(
    '',
    400,
  );

  const [isFilterInitialized, setIsFilterInitialized] = useState(false);

  const totalLoadedTablesRef = useRef(lobbyTables.length);

  useEffect(() => {
    totalLoadedTablesRef.current = lobbyTables.length;
  }, [lobbyTables]);

  const table = useMemo(
    () => tableStore.getActiveTableById(tableId),
    [tableId],
  );

  useEffect(() => {
    setIsFilterInitialized(false);
    const union = tableStore.getUnionByTable(table);
    lobbyTableStore.resetFilter();
    lobbyTableStore.updateFilterOptions({
      clubs: union.club ? [union.club] : [],
      leagues: union.league ? [union.league] : [],
      superLeagues: union.superLeague ? [union.superLeague] : [],
      searchName: debouncedSearchValue,
    });
    setIsFilterInitialized(true);
  }, [table, table?.players?.length]);

  useEffect(() => {
    return () => lobbyTableStore.resetFilter();
  }, []);

  useEffect(() => {
    if (lobbyTableStore.searchFilter.searchName !== debouncedSearchValue) {
      lobbyTableStore.updateFilterOptions({ searchName: debouncedSearchValue });
      setPage(0);
    }
  }, [debouncedSearchValue]);

  const loadLobbyTables = (pageSize: number = PAGE_SIZE): Promise<void> =>
    lobbyTableStore.getLobbyTablePage(0, pageSize).then(response => {
      setLobbyTables(response.content);
      setHasMore(!response.last);
    });

  const loadMoreLobbyTables = useCallback(
    (): Promise<void> =>
      lobbyTableStore
        .getLobbyTablePage(page + 1, PAGE_SIZE)
        .then(response => {
          setLobbyTables(prev => [...prev, ...response.content]);
          setPage(prev => prev + 1);
          setHasMore(!response.last);
        })
        .catch(() => setHasMore(false)),
    [page],
  );

  useEffect(() => {
    if (!isFilterInitialized) {
      return;
    }
    void loadLobbyTables();

    const interval = setInterval(() => {
      void loadLobbyTables(totalLoadedTablesRef.current || PAGE_SIZE);
    }, UPDATE_LOBBY_EXPERT_MODE_TIMEOUT);

    return () => clearInterval(interval);
  }, [
    isFilterInitialized,
    lobbyTableStore.searchFilter,
    lobbyTableStore.sorting.sortProperty,
    lobbyTableStore.sorting.sortDirection,
  ]);

  const onSortDirectionChanged = (direction: SortDirection): void => {
    lobbyTableStore.setSortDirection(direction);
    setPage(0);
  };

  const onSelectedSortPropertyChanged = (
    sortProperty: LobbyTableCustomSortingProperty,
  ): void => {
    lobbyTableStore.setSortProperty(sortProperty);
    setPage(0);
  };

  const loader: React.ReactElement = useMemo(
    () => <div className="p-2 text-center">Loading...</div>,
    [],
  );

  const onWaitListPlayersUpdate = useCallback(
    (tableId: string, playersCount: number) =>
      setLobbyTables(prevState =>
        prevState.map(lobbyTable => {
          if (lobbyTable.tableId === tableId) {
            return { ...lobbyTable, waitlistCount: playersCount };
          }
          return lobbyTable;
        }),
      ),
    [lobbyTables],
  );

  return (
    <div className="LobbyTableExpertMode-Component table-section-expert-mode">
      <div className="lobby-tables-filter d-flex justify-content-between">
        <IconField className="lobby-tables-search" iconPosition="left">
          <InputIcon size={40} className="pi pi-search" />
          <InputText
            className=""
            v-model="value1"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder="Search by name or ID"
            autoComplete="off"
            name="disable-autofill"
          />
        </IconField>
        <SortingInput
          sessionStorageKey="lobby-tables-sorting"
          placeholder="Sorting"
          sortingOptionsEnum={LobbyTableCustomSortingProperty}
          selectedSortProperty={lobbyTableStore.sorting.sortProperty}
          sortDirection={lobbyTableStore.sorting.sortDirection}
          onSortDirectionChanged={onSortDirectionChanged}
          onSelectedSortPropertyChanged={onSelectedSortPropertyChanged}
        />
      </div>
      <InfiniteScroll
        dataLength={lobbyTables.length}
        next={loadMoreLobbyTables}
        hasMore={hasMore}
        loader={loader}
      >
        {lobbyTables.map(lobbyTable => (
          <LobbyTable
            key={`${lobbyTable.tableId}-${lobbyTable.club}-${lobbyTable.league}`}
            width="100%"
            height={110}
            lobbyTable={lobbyTable}
            onWaitListPlayersUpdate={onWaitListPlayersUpdate}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default observer(LobbyTablesExpertMode);
